'use client'

import { useRef, useState } from 'react'
import { Check, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SampleTag } from './primitives'
import { useDemoLoop, useInView, useTween } from './motion-hooks'

// Product illustration: one property as a rent ledger. Sample data; totals are computed.
// Live demo: while the card is on screen, the open rent for 014-3.1 is booked, a
// confirmation appears and the totals count up, then it resets and repeats.
// Hovering (or focusing) a row opens a unit card and pauses the demo.
// The server render and reduced motion show the resting state (3.1 open).
const UNITS = [
  {
    id: '014-0.1',
    floor: 'EG links',
    rooms: '3½',
    area: 78,
    net: 1980,
    advance: 220,
    paid: true,
    tenant: 'Familie Brunner',
    since: '03.2019',
    paidOn: '30.09.2026',
  },
  {
    id: '014-0.2',
    floor: 'EG rechts',
    rooms: '2½',
    area: 58,
    net: 1560,
    advance: 180,
    paid: true,
    tenant: 'L. Meier',
    since: '09.2022',
    paidOn: '01.10.2026',
  },
  {
    id: '014-1.1',
    floor: '1. OG links',
    rooms: '4½',
    area: 96,
    net: 2390,
    advance: 260,
    paid: true,
    tenant: 'Familie Rossi',
    since: '07.2017',
    paidOn: '29.09.2026',
  },
  {
    id: '014-1.2',
    floor: '1. OG rechts',
    rooms: '3½',
    area: 78,
    net: 2020,
    advance: 220,
    paid: true,
    tenant: 'S. Huber',
    since: '11.2020',
    paidOn: '01.10.2026',
  },
  {
    id: '014-2.1',
    floor: '2. OG links',
    rooms: '4½',
    area: 96,
    net: 2420,
    advance: 260,
    paid: true,
    tenant: 'Familie Favre',
    since: '02.2016',
    paidOn: '30.09.2026',
  },
  {
    id: '014-2.2',
    floor: '2. OG rechts',
    rooms: '3½',
    area: 78,
    net: 2050,
    advance: 220,
    paid: true,
    tenant: 'T. Graf',
    since: '05.2023',
    paidOn: '02.10.2026',
  },
  {
    id: '014-3.1',
    floor: '3. OG links',
    rooms: '4½',
    area: 96,
    net: 2190,
    advance: 260,
    paid: false,
    overdueDays: 5,
    tenant: 'M. Keller',
    since: '04.2021',
    paidOn: '06.10.2026',
  },
  {
    id: '014-3.2',
    floor: '3. OG rechts',
    rooms: '3½',
    area: 82,
    net: 2170,
    advance: 240,
    paid: true,
    tenant: 'N. Frei',
    since: '08.2018',
    paidOn: '01.10.2026',
  },
]

const LIVE_UNIT = '014-3.1'
const PHASES = [
  { paid: false, hold: 2600 },
  { paid: true, hold: 4400 },
]
const DUE_DATE = '01.10.2026'
const CARD_ID = 'ledger-unit-card'

const amount = new Intl.NumberFormat('de-CH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const chf = value => `CHF ${amount.format(value)}`

const sum = (key, rows = UNITS) =>
  rows.reduce((total, unit) => total + unit[key], 0)
const gross = unit => unit.net + unit.advance
const TARGET = UNITS.reduce((total, unit) => total + gross(unit), 0)

function StatusChip({ paid, overdueDays, animate }) {
  return paid ? (
    <span
      key="paid"
      className={cn(
        'inline-block rounded-[3px] bg-success-soft px-1.5 py-0.5 font-mono text-[11px] text-success',
        animate && 'fade-up'
      )}
    >
      bezahlt
    </span>
  ) : (
    <span
      key="open"
      className={cn(
        'inline-block whitespace-nowrap rounded-[3px] bg-warning-soft px-1.5 py-0.5 font-mono text-[11px] text-warning',
        animate && 'fade-up'
      )}
    >
      offen · {overdueDays} Tage
    </span>
  )
}

// Floating card with the unit's tenancy details, anchored below (or above) the hovered row.
function UnitCard({ unit, anchor }) {
  const visible = Boolean(unit && anchor)
  return (
    <div
      id={CARD_ID}
      role="tooltip"
      style={anchor ? { top: anchor.y } : undefined}
      className={cn(
        'pointer-events-none absolute right-4 z-20 w-[min(300px,calc(100%-2rem))] rounded-card border border-rule-strong bg-surface p-4 shadow-[0_18px_40px_-16px_rgb(var(--ink)/0.35)] transition-[opacity,transform] duration-200 ease-out',
        anchor?.above && '-translate-y-full',
        visible ? 'opacity-100' : 'invisible opacity-0'
      )}
    >
      {unit && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-xs text-signal">
              {unit.id} · {unit.floor}
            </span>
            <StatusChip paid={unit.paid} overdueDays={unit.overdueDays} />
          </div>
          <p className="text-[15px] font-semibold text-ink">{unit.tenant}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
            <dt className="text-graphite">Mietbeginn</dt>
            <dd className="text-right tabular-nums text-ink">{unit.since}</dd>
            <dt className="text-graphite">Kaution</dt>
            <dd className="text-right tabular-nums text-ink">
              {chf(unit.net * 3)}
            </dd>
            <dt className="text-graphite">Oktober</dt>
            <dd
              className={cn(
                'text-right tabular-nums',
                unit.paid ? 'text-success' : 'text-warning'
              )}
            >
              {unit.paid
                ? `eingegangen ${unit.paidOn}`
                : `fällig seit ${DUE_DATE}`}
            </dd>
          </dl>
          <p className="border-t border-rule pt-2 font-mono text-[11px] text-graphite">
            Kaution: 3 Monatsmieten netto
          </p>
        </div>
      )}
    </div>
  )
}

export function RentLedger({ className }) {
  const ref = useRef(null)
  const rowRefs = useRef({})
  const pausedRef = useRef(false)
  const inView = useInView(ref)
  const phase = useDemoLoop(PHASES, { active: inView, pausedRef })
  const livePaid = PHASES[phase].paid
  const [hover, setHover] = useState(null)

  const units = UNITS.map(unit =>
    unit.id === LIVE_UNIT ? { ...unit, paid: livePaid } : unit
  )
  const received = units
    .filter(unit => unit.paid)
    .reduce((total, unit) => total + gross(unit), 0)
  const openCount = units.filter(unit => !unit.paid).length
  const receivedShown = useTween(received)
  const openShown = useTween(TARGET - received)

  const kpis = [
    { label: 'Soll Oktober', value: chf(TARGET) },
    { label: 'Eingegangen', value: chf(receivedShown) },
    {
      label: `Offen (${openCount})`,
      value: chf(openShown),
      warn: openCount > 0,
    },
    { label: 'Leerstand', value: `0 von ${UNITS.length}` },
  ]
  const liveUnit = UNITS.find(unit => unit.id === LIVE_UNIT)
  const hoveredUnit = hover && units.find(unit => unit.id === hover.id)

  const showCard = (id, index) => {
    const row = rowRefs.current[id]
    const box = ref.current?.getBoundingClientRect()
    if (!row || !box) return
    const rowBox = row.getBoundingClientRect()
    // Lower half of the table opens the card upwards so the figure never clips it.
    const above = index >= UNITS.length / 2
    setHover({
      id,
      above,
      y: above ? rowBox.top - box.top - 6 : rowBox.bottom - box.top + 6,
    })
  }

  return (
    <figure
      ref={ref}
      onMouseEnter={() => {
        pausedRef.current = true
      }}
      onMouseLeave={() => {
        pausedRef.current = false
        setHover(null)
      }}
      className={cn(
        'relative overflow-hidden rounded-card border border-rule-strong bg-surface',
        className
      )}
    >
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute right-4 top-4 z-10 flex items-center gap-2 rounded-control border border-success/25 bg-surface px-3 py-2 text-[13px] shadow-[0_14px_32px_-14px_rgb(var(--ink)/0.35)] transition-all duration-500 ease-out',
          livePaid ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
        )}
      >
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-success text-white">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
        <span className="font-medium text-ink">Zahlung erfasst</span>
        <span className="hidden font-mono text-xs text-graphite sm:inline">
          {LIVE_UNIT} · {chf(gross(liveUnit))}
        </span>
      </div>

      <UnitCard unit={hoveredUnit} anchor={hover} />

      <div className="flex flex-col gap-1.5 border-b border-rule px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          <span className="font-mono text-xs text-graphite">
            Liegenschaften / OBJ-014
          </span>
          <SampleTag />
        </div>
        <p className="text-[22px] font-[450] leading-tight text-ink [font-stretch:110%]">
          Seestrasse 14, 8712 Stäfa
        </p>
        <p className="font-mono text-xs text-graphite">
          Baujahr 1987 · {UNITS.length} Einheiten · {sum('area')} m² ·
          Mehrfamilienhaus
        </p>
      </div>

      <dl className="grid grid-cols-2 border-b border-rule md:grid-cols-4">
        {kpis.map((kpi, i) => (
          <div
            key={kpi.label}
            className={cn(
              'flex flex-col gap-0.5 px-5 py-3 transition-colors duration-200 hover:bg-canvas',
              i % 2 === 1 && 'border-l border-rule',
              i >= 2 && 'border-t border-rule md:border-t-0',
              i === 2 && 'md:border-l'
            )}
          >
            <dt className="font-mono text-[11px] text-graphite">{kpi.label}</dt>
            <dd
              className={cn(
                'text-lg font-medium tabular-nums transition-colors duration-500',
                kpi.warn ? 'text-warning' : 'text-ink'
              )}
            >
              {kpi.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-[13px]">
          <caption className="sr-only">
            Mietzinsliste Oktober für Seestrasse 14, Beispieldaten. Zeilen
            zeigen beim Überfahren oder Fokussieren Details zur Einheit.
          </caption>
          <thead>
            <tr className="bg-canvas text-left font-mono text-[11px] text-graphite">
              <th scope="col" className="px-5 py-2.5 font-normal">
                Einheit
              </th>
              <th scope="col" className="px-3 py-2.5 font-normal">
                Lage
              </th>
              <th scope="col" className="px-3 py-2.5 font-normal">
                Zimmer
              </th>
              <th scope="col" className="px-3 py-2.5 text-right font-normal">
                m²
              </th>
              <th scope="col" className="px-3 py-2.5 text-right font-normal">
                Netto
              </th>
              <th scope="col" className="px-3 py-2.5 text-right font-normal">
                NK akonto
              </th>
              <th scope="col" className="px-3 py-2.5 text-right font-normal">
                Brutto
              </th>
              <th scope="col" className="px-5 py-2.5 font-normal">
                Oktober
              </th>
            </tr>
          </thead>
          <tbody
            className="tabular-nums text-ink"
            onMouseLeave={() => setHover(null)}
          >
            {units.map((unit, index) => {
              const live = unit.id === LIVE_UNIT
              const active = hover?.id === unit.id
              return (
                <tr
                  key={unit.id}
                  ref={node => {
                    rowRefs.current[unit.id] = node
                  }}
                  tabIndex={0}
                  aria-describedby={active ? CARD_ID : undefined}
                  onMouseEnter={() => showCard(unit.id, index)}
                  onFocus={() => {
                    pausedRef.current = true
                    showCard(unit.id, index)
                  }}
                  onBlur={() => {
                    pausedRef.current = false
                    setHover(null)
                  }}
                  className={cn(
                    'group/row cursor-default border-t border-rule outline-none transition-colors duration-200',
                    live &&
                      (unit.paid ? 'bg-success-soft/60' : 'bg-warning-soft/50'),
                    active && 'bg-mist/70',
                    'focus-visible:bg-mist/70'
                  )}
                >
                  <td className="px-5 py-2.5 font-mono text-xs text-signal">
                    {unit.id}
                  </td>
                  <td className="px-3 py-2.5">{unit.floor}</td>
                  <td className="px-3 py-2.5">{unit.rooms}</td>
                  <td className="px-3 py-2.5 text-right">{unit.area}</td>
                  <td className="px-3 py-2.5 text-right">
                    {amount.format(unit.net)}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {amount.format(unit.advance)}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {amount.format(gross(unit))}
                  </td>
                  <td className="px-5 py-2.5">
                    <span className="flex items-center justify-between gap-3">
                      <StatusChip
                        paid={unit.paid}
                        overdueDays={unit.overdueDays}
                        animate={live && inView}
                      />
                      <ChevronRight
                        aria-hidden
                        className={cn(
                          'h-4 w-4 text-signal transition-[opacity,transform] duration-200',
                          active
                            ? 'translate-x-0 opacity-100'
                            : '-translate-x-1 opacity-0'
                        )}
                      />
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot className="tabular-nums">
            <tr className="border-t border-rule-strong bg-canvas font-semibold text-ink">
              <td className="px-5 py-2.5" colSpan={3}>
                Total {UNITS.length} Einheiten
              </td>
              <td className="px-3 py-2.5 text-right">{sum('area')}</td>
              <td className="px-3 py-2.5 text-right">
                {amount.format(sum('net'))}
              </td>
              <td className="px-3 py-2.5 text-right">
                {amount.format(sum('advance'))}
              </td>
              <td className="px-3 py-2.5 text-right">
                {amount.format(TARGET)}
              </td>
              <td className="px-5 py-2.5" />
            </tr>
          </tfoot>
        </table>
      </div>
    </figure>
  )
}
